import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { FadeUp, StaggerWords } from "@/components/Motion";
import BeeTileBackdrop from "@/components/BeeTileBackdrop";
import { ArrowUpRight } from "lucide-react";

const SLIDES = [
  {
    src: "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/y9ajdr3x_watermarked_img_4482118087857854184.png",
    alt: "Madubau Dolomites — Pure Alpine Honey",
    caption: { de: "PURE ALPINE HONEY", en: "PURE ALPINE HONEY", it: "PURE ALPINE HONEY" },
  },
  {
    src: "https://customer-assets-4nw71qhi.emergentagent.net/job_honey-luxury-1/artifacts/wg0ejvlb_4.png",
    alt: "Madubau Dolomiti — Vanilla Alpine Honey",
    caption: { de: "VANILLA ALPINE HONEY", en: "VANILLA ALPINE HONEY", it: "VANILLA ALPINE HONEY" },
  },
];

export default function UnikatSection() {
  const { t, lang } = useI18n();
  const ref = useRef(null);
  const [index, setIndex] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [60, -80]);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), 4500);
    return () => clearInterval(id);
  }, []);

  const label = { de: "PURE & LIMITIERT", en: "PURE & LIMITED", it: "PURO & LIMITATO", ja: "ピュア & 限定", zh: "纯净 · 限量" }[lang] || "PURE & LIMITIERT";
  const eyebrow = { de: "SIGNATURE", en: "SIGNATURE", it: "SIGNATURE", ja: "シグネチャー", zh: "经典之作" }[lang] || "SIGNATURE";
  const body = {
    de: "Madubau Dolomiti — Pure Alpine Honey in seiner feinsten Form. Eine exklusive Auflage, bestimmt vom Rhythmus der Natur. Wir ernten nur das, was die Bienen uns schenken.",
    en: "Madubau Dolomiti — Pure Alpine Honey in its finest form. An exclusive edition, defined by the rhythm of nature. We harvest only what the bees choose to give us.",
    it: "Madubau Dolomiti — Pure Alpine Honey nella sua forma più pregiata. Un'edizione esclusiva, dettata dal ritmo della natura. Raccogliamo soltanto ciò che le api ci donano.",
    ja: "Madubau Dolomiti — Pure Alpine Honeyの最上の姿。自然のリズムに委ねた限定エディション。蜜蜂が分けてくれたものだけを、私たちは摘み取ります。",
    zh: "Madubau Dolomiti——至臻形态的 Pure Alpine Honey。由自然节律决定的限量之作。我们只采收蜜蜂馈赠给我们的那一份。",
  }[lang] || "Madubau Dolomiti — Pure Alpine Honey in seiner feinsten Form. Eine exklusive Auflage, bestimmt vom Rhythmus der Natur. Wir ernten nur das, was die Bienen uns schenken.";
  const cta = { de: "Zur Kollektion", en: "To the Collection", it: "Alla Collezione", ja: "コレクションへ", zh: "前往系列" }[lang] || "Zur Kollektion";

  return (
    <section
      ref={ref}
      className="relative bg-[#0A0A0A] py-40 lg:py-56 overflow-hidden border-t border-white/5"
      data-testid="unikat-section"
    >
      {/* Decorative bee-tile backdrop */}
      <BeeTileBackdrop opacity={0.32} />

      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-16 lg:gap-8 items-center">
          {/* LEFT — Text */}
          <FadeUp className="lg:col-span-6 lg:pr-16 relative z-10">
            <div className="font-display text-[10px] tracking-[0.45em] text-gold mb-6">/ {eyebrow}</div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-8xl tracking-[0.14em] leading-[0.95] text-gold mb-10" style={{ fontWeight: 300 }} data-testid="unikat-heading">
              {label}
            </h2>
            <p className="text-cream/70 font-body text-base lg:text-lg leading-[1.85] max-w-md mb-12">
              {body}
            </p>
            <Link
              to="/shop"
              data-testid="unikat-cta"
              className="inline-flex items-center gap-3 px-10 py-4 border border-gold/60 text-cream hover:text-black hover:bg-gold transition-colors duration-500 font-display text-[11px] tracking-[0.35em] group"
            >
              → {cta}
              <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
            </Link>
          </FadeUp>

          {/* RIGHT — Product image slider */}
          <div className="lg:col-span-6 relative lg:-ml-16">
            <motion.div style={{ y: imgY }} className="relative">
              <div className="relative aspect-[4/5] overflow-hidden clip-frame bg-[#0d0d0d]">
                <div className="absolute inset-0 hero-glow z-0" />
                <AnimatePresence mode="wait" initial={false}>
                  <motion.img
                    key={SLIDES[index].src}
                    src={SLIDES[index].src}
                    alt={SLIDES[index].alt}
                    initial={{ x: "60%", opacity: 0 }}
                    animate={{ x: "0%", opacity: 1 }}
                    exit={{ x: "-60%", opacity: 0 }}
                    transition={{ duration: 1.1, ease: [0.32, 0.72, 0.24, 1] }}
                    className="absolute inset-0 w-full h-full object-cover z-[1]"
                    data-testid={`unikat-slide-${index}`}
                  />
                </AnimatePresence>
              </div>

              {/* Limited Edition label */}
              <div className="absolute top-6 left-6 z-10 border border-gold/60 bg-black/50 backdrop-blur-sm px-5 py-3" data-testid="unikat-badge">
                <div className="font-display text-[9px] tracking-[0.4em] text-gold">— LIMITED EDITION —</div>
              </div>

              {/* Variant caption (bottom-left) */}
              <div className="absolute bottom-6 left-6 z-10">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={SLIDES[index].caption[lang] || SLIDES[index].caption.en}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.6, delay: 0.15 }}
                    className="font-display text-[10px] tracking-[0.4em] text-gold bg-black/50 backdrop-blur-sm border border-gold/40 px-4 py-2"
                  >
                    {SLIDES[index].caption[lang] || SLIDES[index].caption.en}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Slide dots (bottom-right) */}
              <div className="absolute bottom-6 right-6 z-10 flex items-center gap-2" data-testid="unikat-dots">
                {SLIDES.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Slide ${i + 1}`}
                    onClick={() => setIndex(i)}
                    data-testid={`unikat-dot-${i}`}
                    className={`h-[6px] transition-all duration-500 ${
                      i === index ? "w-8 bg-gold" : "w-2 bg-cream/30 hover:bg-cream/60"
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
